<template>
  <div class="space-y-4">
    <!-- Last 3 Games Section -->
    <div class="space-y-2" v-if="hasValidStats">
      <button 
        @click="toggleLast3Games = !toggleLast3Games"
        class="group w-full flex justify-between items-center p-3 bg-white border rounded-lg transition-all duration-200 hover:bg-gray-50 hover:shadow-sm"
        :class="{ 'border-l-4 border-l-red-500': getPercentageDifferenceValue('last3') >= 1000 || getPercentageDifferenceValue('last3') <=-1000 }"
      >
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium text-gray-700 w-24">Last 3 per 30</span>
          <span v-if="getPercentageDifferenceValue('last3') >= 1000" 
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            {{ getPercentageDifferenceValue('last3').toFixed(2) }}%
          </span>
          <span v-else-if="getPercentageDifferenceValue('last3') > 0" 
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {{ getPercentageDifferenceValue('last3').toFixed(2) }}%
          </span>
            <span v-else-if="getPercentageDifferenceValue('last3') <=-1000" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>{{ getPercentageDifferenceValue('last3').toFixed(2) }}%
          </span>
          <span v-else-if="getPercentageDifferenceValue('last3') < 0 " class="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
            {{ getPercentageDifferenceValue('last3').toFixed(2) }}%
          </span>
          
        </div>
        
        <div class="flex items-center gap-2">
          <div class="flex items-center">
            <span class="px-2.5 py-1 text-sm rounded-md bg-gray-100 text-gray-700 font-medium mr-2">
              {{ getLast3Per30Value() }}
            </span>
            
            <span class="px-2.5 py-1 text-xs rounded-md border border-gray-200 text-gray-500">
              {{ getLast3Difference() }}
            </span>
          </div>

          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            class="w-4 h-4 text-gray-400 transition-transform duration-200 group-hover:text-gray-600"
            :class="{ 'rotate-180': toggleLast3Games }"
            fill="none"
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </div>
      </button>
      
      <div v-if="toggleLast3Games" class="bg-white border rounded-xl p-4 space-y-3 shadow-sm">
        <div v-if="hasLast3Games" class="space-y-3">
          <div v-for="(game, index) in additionalStats.last_3_games" :key="index" 
            class="flex justify-between p-3 bg-gray-50 rounded-lg relative hover:bg-gray-100 transition-colors duration-200">
            <div class="space-y-1">
              <div class="text-sm font-medium text-gray-900">{{ formatGameDate(game.game_date) }}</div>
              <div class="flex items-center">
                <span class="px-1.5 py-0.5 text-xs bg-blue-50 text-blue-700 rounded">{{ game.league_name }}</span>
              </div>
            </div>
            <div class="text-right space-y-1">
              <div class="flex items-center gap-1">
                <span class="text-sm font-medium text-gray-900">{{ game[statKey] || game[getGameStatKey()] }} {{ statUnit }}</span>
                <span class="text-xs text-gray-600 bg-gray-200 px-1.5 py-0.5 rounded-md">
                  {{ calculatePerThirty(game[statKey] || game[getGameStatKey()], game.minutes) }}
                </span>
              </div>
              <div class="text-xs text-gray-600">{{ game.minutes }} mins</div>
            </div>
          </div>
          <div class="w-full h-[100px] bg-gray-50 rounded-lg p-4">
            <div class="flex h-full items-end gap-1 flex-row-reverse">
              <div v-for="(game, index) in additionalStats.last_3_games" :key="index"
                class="flex-1 bg-gradient-to-t from-blue-600 to-blue-400 transition-all duration-300 relative hover:from-blue-700 hover:to-blue-500 rounded-t-sm"
                :style="{ height: calculateBarHeight(game[statKey] || game[getGameStatKey()], getMaxStat(additionalStats.last_3_games)) }"
                :title="`${game[statKey] || game[getGameStatKey()]} ${statUnit}`"
                @mouseover="hoveredGameIndex = index"
                @mouseleave="hoveredGameIndex = null">
                <div class="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-medium bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
                  {{ calculatePerThirty(game[statKey] || game[getGameStatKey()], game.minutes, 1) }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-sm text-gray-500 text-center py-4 italic">No games available</div>
      </div>
    </div>

    <!-- Last 6 Games Section -->
    <div class="space-y-2" v-if="hasValidStats">
      <button 
        @click="toggleLast6Games = !toggleLast6Games"
        class="group w-full flex justify-between items-center p-3 bg-white border rounded-lg transition-all duration-200 hover:bg-gray-50 hover:shadow-sm"
        :class="{ 'border-l-4 border-l-red-500': getPercentageDifferenceValue('last6') >= 40 || getPercentageDifferenceValue('last6') <=-40 }"
      >
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium text-gray-700 w-24">Last 6 per 30</span>
          <span v-if="getPercentageDifferenceValue('last6') >= 40" 
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            {{ getPercentageDifferenceValue('last6').toFixed(2) }}%
          </span>
          <span v-else-if="getPercentageDifferenceValue('last6') > 0" 
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {{ getPercentageDifferenceValue('last6').toFixed(2) }}%
          </span>
            <span v-else-if="getPercentageDifferenceValue('last6') <=-40" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
              {{ getPercentageDifferenceValue('last6').toFixed(2) }}%
          </span>
          <span v-else-if="getPercentageDifferenceValue('last6') < 0 " 
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {{ getPercentageDifferenceValue('last6').toFixed(2) }}%
          </span>
        </div>
        
        <div class="flex items-center gap-2">
          <div class="flex items-center">
            <span class="px-2.5 py-1 text-sm rounded-md bg-gray-100 text-gray-700 font-medium mr-2">
              {{ getLast6Per30Value() }}
            </span>
            
            <span class="px-2.5 py-1 text-xs rounded-md border border-gray-200 text-gray-500">
              {{ getLast6Difference() }}
            </span>
          </div>

          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            class="w-4 h-4 text-gray-400 transition-transform duration-200 group-hover:text-gray-600"
            :class="{ 'rotate-180': toggleLast6Games }"
            fill="none"
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </div>
      </button>
      
      <div v-if="toggleLast6Games" class="bg-white border rounded-xl p-4 space-y-3 shadow-sm">
        <div v-if="hasLast6Games" class="space-y-3">
          <div v-for="(game, index) in additionalStats.last_6_games" :key="index" 
            class="flex justify-between p-3 bg-gray-50 rounded-lg relative hover:bg-gray-100 transition-colors duration-200">
            <div class="space-y-1">
              <div class="text-sm font-medium text-gray-900">{{ formatGameDate(game.game_date) }}</div>
              <div class="flex items-center">
                <span class="px-1.5 py-0.5 text-xs bg-blue-50 text-blue-700 rounded">{{ game.league_name }}</span>
              </div>
            </div>
            <div class="text-right space-y-1">
              <div class="flex items-center gap-1">
                <span class="text-sm font-medium text-gray-900">{{ game[statKey] || game[getGameStatKey()] }} {{ statUnit }}</span>
                <span class="text-xs text-gray-600 bg-gray-200 px-1.5 py-0.5 rounded-md">
                  {{ calculatePerThirty(game[statKey] || game[getGameStatKey()], game.minutes) }}
                </span>
              </div>
              <div class="text-xs text-gray-600">{{ game.minutes }} mins</div>
            </div>
          </div>
          <div class="w-full h-[100px] bg-gray-50 rounded-lg p-4">
            <div class="flex h-full items-end gap-1 flex-row-reverse">
              <div v-for="(game, index) in additionalStats.last_6_games" :key="index"
                class="flex-1 bg-gradient-to-t from-red-600 to-red-400 transition-all duration-300 relative hover:from-red-700 hover:to-red-500 rounded-t-sm"
                :style="{ height: calculateBarHeight(game[statKey] || game[getGameStatKey()], getMaxStat(additionalStats.last_6_games)) }"
                :title="`${game[statKey] || game[getGameStatKey()]} ${statUnit}`"
                @mouseover="hoveredGameIndex = index"
                @mouseleave="hoveredGameIndex = null">
                <div class="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-medium bg-red-100 text-red-800 px-1.5 py-0.5 rounded">
                  {{ calculatePerThirty(game[statKey] || game[getGameStatKey()], game.minutes, 1) }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-sm text-gray-500 text-center py-4 italic">No games available</div>
      </div>
    </div>

    <!-- Last 10 Games Section -->
    <div class="space-y-2" v-if="hasValidStats">
      <button 
        @click="toggleLast10Games = !toggleLast10Games"
        class="group w-full flex justify-between items-center p-3 bg-white border rounded-lg transition-all duration-200 hover:bg-gray-50 hover:shadow-sm"
        :class="{ 'border-l-4 border-l-red-500': getPercentageDifferenceValue('last10') >= 20 || getPercentageDifferenceValue('last10') <=-20 }"
      >
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium text-gray-700 w-24">Last 10 per 30</span>
          <span v-if="getPercentageDifferenceValue('last10') >= 20" 
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            {{ getPercentageDifferenceValue('last10').toFixed(2) }}%
          </span>
          <span v-else-if="getPercentageDifferenceValue('last10') > 0" 
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {{ getPercentageDifferenceValue('last10').toFixed(2) }}%
          </span>
          <span v-else-if="getPercentageDifferenceValue('last10') <=-20" class="text-xs bg-red-800 text-white px-2 py-0.5 rounded-full">
            {{ getPercentageDifferenceValue('last10').toFixed(2) }}%
          </span>
          <span v-else-if="getPercentageDifferenceValue('last10') < 0" 
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {{ getPercentageDifferenceValue('last10').toFixed(2) }}%
          </span>
        </div>
        
        <div class="flex items-center gap-2">
          <div class="flex items-center">
            <span class="px-2.5 py-1 text-sm rounded-md bg-gray-100 text-gray-700 font-medium mr-2">
              {{ getLast10Per30Value() }}
            </span>
            
            <span class="px-2.5 py-1 text-xs rounded-md border border-gray-200 text-gray-500">
              {{ getLast10Difference() }}
            </span>
          </div>

          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            class="w-4 h-4 text-gray-400 transition-transform duration-200 group-hover:text-gray-600"
            :class="{ 'rotate-180': toggleLast10Games }"
            fill="none"
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </div>
      </button>
      
      <div v-if="toggleLast10Games" class="bg-white border rounded-xl p-4 space-y-3 shadow-sm">
        <div v-if="hasLast10Games" class="space-y-3">
          <div v-for="(game, index) in additionalStats.last_10_games" :key="index" 
            class="flex justify-between p-3 bg-gray-50 rounded-lg relative hover:bg-gray-100 transition-colors duration-200">
            <div class="space-y-1">
              <div class="text-sm font-medium text-gray-900">{{ formatGameDate(game.game_date) }}</div>
              <div class="flex items-center">
                <span class="px-1.5 py-0.5 text-xs bg-blue-50 text-blue-700 rounded">{{ game.league_name }}</span>
              </div>
            </div>
            <div class="text-right space-y-1">
              <div class="flex items-center gap-1">
                <span class="text-sm font-medium text-gray-900">{{ game[statKey] || game[getGameStatKey()] }} {{ statUnit }}</span>
                <span class="text-xs text-gray-600 bg-gray-200 px-1.5 py-0.5 rounded-md">
                  {{ calculatePerThirty(game[statKey] || game[getGameStatKey()], game.minutes) }}
                </span>
              </div>
              <div class="text-xs text-gray-600">{{ game.minutes }} mins</div>
            </div>
          </div>
          <div class="w-full h-[100px] bg-gray-50 rounded-lg p-4">
            <div class="flex h-full items-end gap-1 flex-row-reverse">
              <div v-for="(game, index) in additionalStats.last_10_games" :key="index"
                class="flex-1 bg-gradient-to-t from-red-600 to-red-400 transition-all duration-300 relative hover:from-red-700 hover:to-red-500 rounded-t-sm"
                :style="{ height: calculateBarHeight(game[statKey] || game[getGameStatKey()], getMaxStat(additionalStats.last_10_games)) }"
                :title="`${game[statKey] || game[getGameStatKey()]} ${statUnit}`"
                @mouseover="hoveredGameIndex = index"
                @mouseleave="hoveredGameIndex = null">
                <div class="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-medium bg-red-100 text-red-800 px-1.5 py-0.5 rounded">
                  {{ calculatePerThirty(game[statKey] || game[getGameStatKey()], game.minutes, 1) }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-sm text-gray-500 text-center py-4 italic">No games available</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

defineOptions({
  name: 'AdditionalStatsDisplay'
});

const props = defineProps({
  additionalStats: {
    type: Object,
    required: true,
    default: () => ({})
  },
  statKey: {
    type: String,
    default: 'points'
  },
  statPer30Key: {
    type: String,
    default: 'points_per_30'
  },
  statUnit: {
    type: String,
    default: 'pts'
  },
  originalPer30: {
    type: [Number, String],
    default: 0
  },
  percentageDifference: {
    type: Object,
    default: () => ({})
  }
});

// Local state
const hoveredGameIndex = ref(null);
const toggleLast3Games = ref(false);
const toggleLast6Games = ref(false);
const toggleLast10Games = ref(false);

// Parse originalPer30 to ensure it's a number
const parsedOriginalPer30 = computed(() => {
  return typeof props.originalPer30 === 'string' ? parseFloat(props.originalPer30) || 0 : props.originalPer30 || 0;
});

// Helper functions for percentage calculations
const getPercentageDifferenceValue = (key) => {
  if (!props.percentageDifference || !props.percentageDifference[key]) return 0;
  const value = props.percentageDifference[key];
  return typeof value === 'number' ? value : parseFloat(value) || 0;
};

// Computed properties for safety checks
const hasValidStats = computed(() => {
  return props.additionalStats && 
         props.additionalStats.last_games_avg && 
         typeof props.additionalStats.last_games_avg === 'object';
});

const hasLast3Games = computed(() => {
  return props.additionalStats && 
         props.additionalStats.last_3_games && 
         Array.isArray(props.additionalStats.last_3_games) && 
         props.additionalStats.last_3_games.length > 0;
});

const hasLast6Games = computed(() => {
  return props.additionalStats && 
         props.additionalStats.last_6_games && 
         Array.isArray(props.additionalStats.last_6_games) && 
         props.additionalStats.last_6_games.length > 0;
});

const hasLast10Games = computed(() => {
  return props.additionalStats && 
         props.additionalStats.last_10_games && 
         Array.isArray(props.additionalStats.last_10_games) && 
         props.additionalStats.last_10_games.length > 0;
});

// Helper functions to get values from the API response
const getLast3Per30Value = () => {
  if (!props.additionalStats?.last_games_per_30?.last_3_games_per_30) return '0';
  const value = props.additionalStats.last_games_per_30.last_3_games_per_30[props.statPer30Key];
  return value !== undefined && value !== null ? parseFloat(value).toFixed(2) : '0';
};

const getLast3Difference = () => {
  const last3Value = parseFloat(getLast3Per30Value()) || 0;
  const originalValue = parsedOriginalPer30.value;
  return (originalValue - last3Value).toFixed(2);
};

const getLast6Per30Value = () => {
  if (!props.additionalStats?.last_games_per_30?.last_6_games_per_30) return '0';
  const value = props.additionalStats.last_games_per_30.last_6_games_per_30[props.statPer30Key];
  return value !== undefined && value !== null ? parseFloat(value).toFixed(2) : '0';
};

const getLast6Difference = () => {
  const last6Value = parseFloat(getLast6Per30Value()) || 0;
  const originalValue = parsedOriginalPer30.value;
  return (originalValue - last6Value).toFixed(2);
};

const getLast10Per30Value = () => {
  if (!props.additionalStats?.last_games_per_30?.last_10_games_per_30) return '0';
  const value = props.additionalStats.last_games_per_30.last_10_games_per_30[props.statPer30Key];
  return value !== undefined && value !== null ? parseFloat(value).toFixed(2) : '0';
};

const getLast10Difference = () => {
  const last10Value = parseFloat(getLast10Per30Value()) || 0;
  const originalValue = parsedOriginalPer30.value;
  return (originalValue - last10Value).toFixed(2);
};

// Helper functions
const safeStatsValue = (value) => {
  return value !== undefined && value !== null ? value : '0';
};

const formatGameDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString('et-EE', { timeZone: 'Europe/Tallinn' });
  } catch (e) {
    return dateString;
  }
};

const calculatePerThirty = (points, minutes, decimals = 2) => {
  if (!points || !minutes || minutes === 0) return '0.00';
  return ((points / minutes) * 30).toFixed(decimals);
};

const getMaxStat = (games) => {
  if (!games || !Array.isArray(games) || games.length === 0) return 0;
  return Math.max(...games.map(g => parseFloat(g[props.statKey] || g[getGameStatKey()]) || 0));
};

const calculateBarHeight = (statValue, maxValue) => {
  if (!statValue || !maxValue || maxValue === 0) return '0%';
  return `${(statValue / maxValue * 100)}%`;
};

const getGameStatKey = () => {
  // Map stat keys to the correct field names in the game data
  const statKeyMapping = {
    'points': 'points',
    'points_per_30': 'points',
    'rebounds': 'total_rebounds',
    'total_rebounds': 'total_rebounds',
    'total_rebounds_per_30': 'total_rebounds',
    'assists': 'assists',
    'assists_per_30': 'assists',
    'steals': 'steals',
    'steals_per_30': 'steals',
    'blocks': 'blocks',
    'blocks_per_30': 'blocks',
    'turnovers': 'turnovers',
    'turnovers_per_30': 'turnovers',
    'three_point_made': 'three_point_field_goal',
    'three_point_field_goal': 'three_point_field_goal',
    'three_point_field_goal_per_30': 'three_point_field_goal',
    'pra': 'pra',
    'pra_per_30': 'pra'
  };
  
  return statKeyMapping[props.statKey] || props.statKey;
};
</script> 